import { logger } from '../../utils/logger';
import type { MigrationLogsResponse, LogFilters } from '../../types/logs';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

/**
 * Fetch logs for a specific migration from the backend API
 * @param bearerToken - Authentication token
 * @param migrationId - ID of the migration to fetch logs for
 * @param filters - Filter and pagination parameters
 * @returns Promise resolving to MigrationLogsResponse
 */
export const fetchMigrationLogsFromService = async (
  bearerToken: string,
  migrationId: string,
  filters: LogFilters
): Promise<MigrationLogsResponse> => {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;

    // Build query parameters
    const params = new URLSearchParams();

    // Add stage filters (can be multiple)
    if (filters.stages.length > 0) {
      filters.stages.forEach((stage) => params.append('stage', stage));
    }

    // Add level filters (can be multiple)
    if (filters.levels.length > 0) {
      filters.levels.forEach((level) => params.append('level', level));
    }

    // Add search term
    if (filters.searchTerm) {
      params.append('search', filters.searchTerm);
    }

    // Add sorting
    params.append('sortBy', filters.sortBy);
    params.append('sortDirection', filters.sortDirection);

    // Add pagination
    params.append('page', filters.page.toString());
    params.append('pageSize', filters.pageSize.toString());

    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/logs?${params.toString()}`;

    logger.info('Fetching migration logs', {
      migrationId,
      filters
    });

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(bearerToken)
    });

    if (!response.ok) {
      logger.error('Fetch migration logs API returned error', {
        status: response.status,
        statusText: response.statusText,
        migrationId,
        filters: JSON.stringify(filters),
        url
      });
      throw new Error(`Error fetching migration logs: ${response.status} ${response.statusText}`);
    }

    const data: MigrationLogsResponse = await response.json();

    logger.info('Migration logs fetched successfully', {
      migrationId,
      totalCount: data.totalCount,
      logCount: data.logs.length,
      page: filters.page,
      pageSize: filters.pageSize
    });

    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to fetch migration logs', {
      error: msg,
      migrationId,
      filters: JSON.stringify(filters)
    });
    throw error;
  }
};

export default fetchMigrationLogsFromService;
